'use client';

import FaIcon from '@/components/icon/FaIcon/FaIcon';
import { faChevronLeft, faChevronRight, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import styles from './CarouselModal.module.scss';

function CarouselModal({
  initShowIndex,
  isOpen,
  close,
  childrenArr,
}: {
  initShowIndex: number;
  isOpen: boolean;
  close: () => void;
  childrenArr: React.ReactNode[];
}) {
  const [showChildren, setShowChildren] = useState(childrenArr[initShowIndex]);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (!isTransitioning) {
        const currentIndex = childrenArr.indexOf(showChildren);
        const nextIndex = currentIndex + 1;
        if (nextIndex < childrenArr.length) {
          setIsTransitioning(true);
          setShowChildren(childrenArr[nextIndex]);
          setTimeout(() => setIsTransitioning(false), 300);
        } else {
          setIsTransitioning(true);
          setShowChildren(childrenArr[0]);
          setTimeout(() => setIsTransitioning(false), 300);
        }
      }
    },
    onSwipedRight: () => {
      if (!isTransitioning) {
        const currentIndex = childrenArr.indexOf(showChildren);
        const prevIndex = currentIndex - 1;
        if (prevIndex >= 0) {
          setIsTransitioning(true);
          setShowChildren(childrenArr[prevIndex]);
          setTimeout(() => setIsTransitioning(false), 300);
        } else {
          setIsTransitioning(true);
          setShowChildren(childrenArr[childrenArr.length - 1]);
          setTimeout(() => setIsTransitioning(false), 300);
        }
      }
    },
    trackMouse: true,
    trackTouch: true,
    preventScrollOnSwipe: true,
  });

  useEffect(() => {
    setShowChildren(childrenArr[initShowIndex]);
  }, [initShowIndex, childrenArr]);

  const onNext = () => {
    if (!isTransitioning) {
      const currentIndex = childrenArr.indexOf(showChildren);
      const nextIndex = currentIndex + 1;
      if (nextIndex < childrenArr.length) {
        setIsTransitioning(true);
        setShowChildren(childrenArr[nextIndex]);
        setTimeout(() => setIsTransitioning(false), 300);
        return;
      }
      setIsTransitioning(true);
      setShowChildren(childrenArr[0]);
      setTimeout(() => setIsTransitioning(false), 300);
    }
  };

  const onPrev = () => {
    if (!isTransitioning) {
      const currentIndex = childrenArr.indexOf(showChildren);
      const prevIndex = currentIndex - 1;
      if (prevIndex >= 0) {
        setIsTransitioning(true);
        setShowChildren(childrenArr[prevIndex]);
        setTimeout(() => setIsTransitioning(false), 300);
        return;
      }
      setIsTransitioning(true);
      setShowChildren(childrenArr[childrenArr.length - 1]);
      setTimeout(() => setIsTransitioning(false), 300);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={close}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} {...handlers}>
        <button type='button' className={styles.closeButton} onClick={close}>
          <FaIcon icon={faXmark} />
        </button>
        <div className={styles.contentsContainer}>
          <button type='button' className={`${styles.navButton} ${styles.navButtonPrev}`} onClick={onPrev}>
            <FaIcon icon={faChevronLeft} />
          </button>
          <div className={styles.contentsItem}>{showChildren}</div>
          <button type='button' className={`${styles.navButton} ${styles.navButtonNext}`} onClick={onNext}>
            <FaIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default CarouselModal;
